import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { User, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { saveNext, peekNext, consumeNext } from '@/lib/authRedirect';
import authIllustration from '@/assets/auth-illustration.png';

const LoginPage = ({ onSwitchToRegister }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signInWithEmail, signInWithGoogle, isAuthenticated } = useAuth();

  // ADO #480 — capture ?next=... if the user landed on /login directly, and
  // pick up any next persisted from the earlier register/verify step.
  useEffect(() => {
    saveNext(searchParams.get('next'));
  }, [searchParams]);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Redirect if already authenticated (honoring any pending ?next=).
  useEffect(() => {
    if (isAuthenticated) {
      navigate(consumeNext('/dashboard'));
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    if (!password.trim()) {
      toast.error('Please enter your password');
      return;
    }

    setIsLoading(true);
    
    try {
      const { error } = await signInWithEmail(email, password);
      
      if (error) {
        const errorMessage = error?.message || 'Invalid email or password';
        toast.error(errorMessage);
      } else {
        toast.success('Welcome back!');
        navigate(consumeNext('/dashboard'));
      }
    } catch (err) {
      console.error('Login error:', err);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      const { error } = await signInWithGoogle(peekNext() || '/dashboard');
      if (error) {
        const errorMessage = error?.message || 'Failed to sign in with Google';
        toast.error(errorMessage);
        setGoogleLoading(false);
      }
      // Redirect happens automatically via OAuth flow
    } catch (err) {
      console.error('Google login error:', err);
      toast.error('An unexpected error occurred. Please try again.');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Illustration */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#e8f4fc] items-center justify-center p-12">
        <div className="max-w-lg">
          <img
            src={authIllustration}
            alt="Student studying"
            className="w-full h-auto"
          />
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-2">
              {/* Logo Icon - Flow -> Code -> Master */}
              <div className="relative w-16 h-16">
                <svg viewBox="0 0 64 64" fill="none" className="w-full h-full">
                  {/* Flow - curved arrow/path */}
                  <path d="M8 20 Q20 8 32 16" stroke="#3b82f6" strokeWidth="3" fill="none" strokeLinecap="round"/>
                  <circle cx="8" cy="20" r="4" fill="#3b82f6"/>
                  
                  {/* Code - brackets in middle */}
                  <path d="M26 28L22 34L26 40" stroke="#f97316" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M38 28L42 34L38 40" stroke="#f97316" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  
                  {/* Master - graduation cap at end */}
                  <path d="M50 44L40 50L50 56L60 50L50 44Z" fill="#10b981"/>
                  <path d="M44 52V58" stroke="#10b981" strokeWidth="2"/>
                  <path d="M56 52V58" stroke="#10b981" strokeWidth="2"/>
                  <circle cx="60" cy="52" r="2" fill="#f97316"/>
                  <path d="M60 50V54" stroke="#f97316" strokeWidth="1.5"/>
                  
                  {/* Connecting flow arrows */}
                  <path d="M32 18L28 24" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeDasharray="2 2"/>
                  <path d="M42 38L46 44" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeDasharray="2 2"/>
                </svg>
              </div>
              {/* Logo Text */}
              <div className="flex flex-col items-start">
                <span className="text-4xl font-bold tracking-tight">
                  <span className="text-[#3b82f6]">F</span>
                  <span className="text-[#f97316]">2</span>
                  <span className="text-[#10b981]">C</span>
                  <span className="text-slate-700 ml-2">Academy</span>
                </span>
              </div>
            </div>
            <p className="text-gray-500 text-sm font-medium">
              <span className="text-[#3b82f6]">Flow First</span>
              <span className="text-gray-400 mx-1">•</span>
              <span className="text-[#f97316]">Code Next</span>
              <span className="text-gray-400 mx-1">•</span>
              <span className="text-[#10b981]">Master Forever</span>
            </p>
          </div>

          {/* Welcome Text */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Welcome Back! <span className="inline-block">👋</span>
            </h1>
            <p className="text-gray-500 text-sm">
              Please sign in to your account and start the adventure
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email or Username
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Type your username"
                  className="pl-10 h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Current Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  data-testid="login-remember-checkbox"
                />
                <label htmlFor="remember" className="text-sm text-gray-600 cursor-pointer">
                  Remember Me
                </label>
              </div>
              <a
                href="#"
                className="text-sm text-blue-500 hover:text-blue-600 hover:underline"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/forgot-password');
                }}
                data-testid="login-forgot-password"
              >
                Forgot Password?
              </a>
            </div>

            {/* Sign In Button */}
            <Button
              type="submit"
              disabled={isLoading || googleLoading}
              className="w-full h-12 bg-[#7c8db5] hover:bg-[#6b7ca4] text-white font-medium text-base"
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Create Account */}
          <p className="text-center mt-6 text-sm text-gray-500">
            New on our platform?{' '}
            <a
              href="#"
              className="text-blue-500 hover:text-blue-600 hover:underline font-medium"
              onClick={(e) => {
                e.preventDefault();
                if (onSwitchToRegister) {
                  onSwitchToRegister();
                }
              }}
              data-testid="login-register-link"
            >
              Create an account
            </a>
          </p>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-400">or</span>
            </div>
          </div>

          {/* Social Login */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 border-gray-200 hover:bg-gray-50 text-gray-700 font-medium"
            onClick={handleGoogleLogin}
            disabled={isLoading || googleLoading}
            data-testid="login-google-btn"
          >
            {googleLoading ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            )}
            Login with Google
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
